// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { Feedback, ProjectPayload } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// mui
import { Alert, AlertTitle, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// props
export interface DialogUpdateProjectProps {
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: () => void;
	payload: Partial<ProjectPayload>;
}

export const DialogUpdateProject = ({
	isOpen,
	onClose,
	onSuccess,
	payload,
}: DialogUpdateProjectProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project, setProject } = useContext(ContextProject);

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	// handlers
	const handleSubmit = async (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		// set loading
		setIsLoading(true);

		try {
			const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${project?.id}`, {
				method: 'PATCH',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify(payload),
			});

			const result = await response.json();

			if (result.error) {
				throw new Error(result.error.message);
			}

			const projectUpdated = { ...project, ...result.data };

			// set banner
			setBanner({
				hasClose: true,
				message: t('alert.projectUpdated'),
				severity: 'success',
			});

			// Update project context with new values
			if (result.data && setProject) {
				setProject((projectPrev) => {
					if (!projectPrev) return projectPrev;
					return {
						...projectPrev,
						currencies: projectUpdated.currencies,
						fiscalYearStart: projectUpdated.fiscalYearStart,
						fiscalYearEnd: projectUpdated.fiscalYearEnd,
					};
				});
			}

			// success callback
			if (onSuccess) {
				onSuccess();
			}

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			setFeedback({
				alert: {
					children: t('error.default'),
				},
			});
		} finally {
			// clear loading
			setIsLoading(false);
		}
	};

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		if (onClose) {
			onClose(e);
		}
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					children: t('saveProject'),
					loading: isLoading,
					onClick: handleSubmit,
					variant: 'contained',
				},
			]}
			alignItems="center"
			IconCircleProps={{ type: 'project' }}
			isOpen={isOpen}
			maxWidth={400}
			onClose={handleClose}
			title={{
				children: t('saveProject'),
			}}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Typography>{t('dialogUpdateProject.content')}</Typography>
				</Grid>
				<Grid item xs={12}>
					<Alert severity="warning">{t('alert.updateProject')}</Alert>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</Grid>
		</DialogActions>
	);
};
